import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AlertConfig {
  show: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export interface ToastConfig {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number; // en milisegundos, 0 = no auto-close
  showClose?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private alertSubject = new BehaviorSubject<AlertConfig>({
    show: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'Aceptar',
    showCancel: false
  });
  private toastsSubject = new BehaviorSubject<ToastConfig[]>([]);
  public toasts$: Observable<ToastConfig[]> = this.toastsSubject.asObservable();


  public alert$: Observable<AlertConfig> = this.alertSubject.asObservable();

  /**
   * Muestra una alerta de éxito
   */
  showSuccess(message: string, title: string = 'Éxito', onConfirm?: () => void): void {
    this.showAlert({
      show: true,
      type: 'success',
      title,
      message,
      confirmText: 'Aceptar',
      showCancel: false,
      onConfirm: onConfirm || (() => this.hideAlert())
    });
  }

  /**
   * Muestra una alerta de error
   */
  showError(message: string, title: string = 'Error', onConfirm?: () => void): void {
    this.showAlert({
      show: true,
      type: 'error',
      title,
      message,
      confirmText: 'Aceptar',
      showCancel: false,
      onConfirm: onConfirm || (() => this.hideAlert())
    });
  }

  /**
   * Muestra una alerta de advertencia
   */
  showWarning(message: string, title: string = 'Advertencia', onConfirm?: () => void, onCancel?: () => void): void {
    this.showAlert({
      show: true,
      type: 'warning',
      title,
      message,
      confirmText: 'Continuar',
      cancelText: 'Cancelar',
      showCancel: true,
      onConfirm: onConfirm || (() => this.hideAlert()),
      onCancel: onCancel || (() => this.hideAlert())
    });
  }

  /**
   * Muestra una alerta de información
   */
  showInfo(message: string, title: string = 'Información', onConfirm?: () => void): void {
    this.showAlert({
      show: true,
      type: 'info',
      title,
      message,
      confirmText: 'Aceptar',
      showCancel: false,
      onConfirm: onConfirm || (() => this.hideAlert())
    });
  }

  /**
   * Muestra una alerta de confirmación
   */
  showConfirm(
    message: string, 
    title: string = 'Confirmar', 
    onConfirm?: () => void, 
    onCancel?: () => void,
    confirmText: string = 'Confirmar',
    cancelText: string = 'Cancelar'
  ): void {
    this.showAlert({
      show: true,
      type: 'warning',
      title,
      message,
      confirmText,
      cancelText,
      showCancel: true,
      onConfirm: onConfirm || (() => this.hideAlert()),
      onCancel: onCancel || (() => this.hideAlert())
    });
  }

  /**
   * Muestra una alerta de error HTTP con manejo específico
   */
  showHttpError(error: any, defaultMessage: string = 'Error en la operación'): void {
    let title = 'Error';
    let message = defaultMessage;

    if (error?.error?.message) {
      message = error.error.message;
    } else if (error?.message) {
      message = error.message;
    }

    // Personalizar título según el código de estado
    if (error?.status === 400) {
      title = 'Error de Validación';
    } else if (error?.status === 401) {
      title = 'No Autorizado';
    } else if (error?.status === 403) {
      title = 'Acceso Denegado';
    } else if (error?.status === 404) {
      title = 'Recurso No Encontrado';
    } else if (error?.status === 500) {
      title = 'Error del Servidor';
    } else if (error?.status === 0) {
      title = 'Error de Conexión';
      message = 'No se pudo conectar con el servidor. Verifique su conexión a internet.';
    }

    this.showError(message, title);
  }

  /**
   * Muestra una alerta personalizada
   */
  showAlert(config: AlertConfig): void {
    this.alertSubject.next(config);
  }

  /**
   * Oculta la alerta actual
   */
  hideAlert(): void {
    this.alertSubject.next({
      show: false,
      type: 'info',
      title: '',
      message: '',
      confirmText: 'Aceptar',
      showCancel: false
    });
  }

  /**
   * Obtiene la configuración actual de la alerta
   */
  getCurrentAlert(): AlertConfig {
    return this.alertSubject.value;
  }


  showSuccessToast(message: string, title: string = 'Éxito', duration: number = 5000): void {
    this.showToast({
      id: this.generateId(),
      type: 'success',
      title,
      message,
      duration,
      showClose: true
    });
  }

  /**
   * Muestra un toast de error
   */
  showErrorToast(message: string, title: string = 'Error', duration: number = 7000): void {
    console.log('showErrorToast', message, title, duration);
    this.showToast({
      id: this.generateId(),
      type: 'error',
      title,
      message,
      duration,
      showClose: true
    });
  }

  /**
   * Muestra un toast de advertencia
   */
  showWarningToast(message: string, title: string = 'Advertencia', duration: number = 6000): void {
    this.showToast({
      id: this.generateId(),
      type: 'warning',
      title,
      message,
      duration,
      showClose: true
    });
  }

  /**
   * Muestra un toast de información
   */
  showInfoToast(message: string, title: string = 'Información', duration: number = 5000): void {
    this.showToast({
      id: this.generateId(),
      type: 'info',
      title,
      message,
      duration,
      showClose: true
    });
  }

  /**
   * Muestra un toast personalizado
   */
  showToast(config: ToastConfig): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, config]);

    // Auto-remove si tiene duración
    if (config.duration && config.duration > 0) {
      setTimeout(() => {
        this.removeToast(config.id);
      }, config.duration);
    }
  }

  /**
   * Remueve un toast específico
   */
  removeToast(id: string): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next(currentToasts.filter(toast => toast.id !== id));
  }

  /**
   * Limpia todos los toasts
   */
  clearAllToasts(): void {
    this.toastsSubject.next([]);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }
}